import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import "./SubwaySpotCheckAllPage.css";

const fields = [
  ["net_sales", "Net Sales"],
  ["cogs_amount", "Food Cost"],
  ["payroll_amount", "Payroll"],
  ["payroll_tax_amount", "Payroll Tax"],
  ["fixed_cost_total", "Fixed Costs"],
  ["profit_loss_total", "Profit / Loss"],
];

const currency = (value) => Number(value || 0).toLocaleString("en-US", {
  style: "currency", currency: "USD",
});

// Treat SQL DATE as a calendar day, never as UTC midnight in the user's timezone.
const calendarDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return "No sales date available";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
};

export default function SubwaySpotCheckAllPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/subway-spotcheck-all/latest");
      setReport(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the Subway spot check.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <main className="payroll-sync-content subway-all-page">
      <div className="avg-cogs-container">
        <header className="subway-all-heading">
          <div>
            <h1 className="pw-page-title">Subway Daily Spot Check</h1>
            <p>{report ? calendarDate(report.date) : "Loading latest sales date…"}</p>
            {report && <p>{report.stores_with_data} of {report.store_count} stores have sales for this date</p>}
          </div>
        </header>

        {error && <p role="alert" className="subway-all-error">{error}</p>}
        {loading && !report && <p>Loading spot check…</p>}
        {!loading && !error && report?.store_count === 0 && <p>No Subway businesses are configured.</p>}
        {!loading && !error && report?.store_count > 0 && !report.date && <p>No Subway sales have been synced yet.</p>}

        {report?.date && (
          <div className="subway-all-scroll">
            <table className="subway-all-table">
              <thead>
                <tr>
                  <th scope="col">Store</th>
                  {fields.map(([key, title]) => <th scope="col" key={key}>{title}</th>)}
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => (
                  <React.Fragment key={row.business_id}>
                    <tr className={row.has_data ? "" : "subway-all-missing"}>
                      <th scope="row">
                        <Link to={`/dashboard/business/${row.business_id}/subway-spotcheck`}>
                          {row.business_name || `Store ${row.store_number}`}
                        </Link>
                        <small>#{row.store_number}</small>
                      </th>
                      {row.has_data ? fields.map(([key]) => (
                        <td key={key} className={key === "profit_loss_total" && row[key] < 0 ? "subway-all-negative" : ""}>
                          {currency(row[key])}
                        </td>
                      )) : <td colSpan={fields.length}>No sales synced for {calendarDate(report.date)}</td>}
                      <td>
                        {row.has_data && (
                          <button type="button" className="avg-cogs-btn"
                            aria-expanded={expanded === row.business_id}
                            onClick={() => setExpanded(expanded === row.business_id ? null : row.business_id)}>
                            {expanded === row.business_id ? "Hide" : "Show"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {row.has_data && expanded === row.business_id && (
                      <tr className="subway-all-details">
                        <td colSpan={fields.length + 2}>
                          <div className="subway-all-detail-grid">
                            <section>
                              <h2>Food cost</h2>
                              <p>Weighted COGS: {Number(row.cogs_percentage || 0).toFixed(2)}%</p>
                              <p>Food cost: {currency(row.cogs_amount)}</p>
                            </section>
                            <section>
                              <h2>Payroll</h2>
                              {row.payroll_rows?.length ? (
                                <ul>{row.payroll_rows.map((person, index) => (
                                  <li key={`${person.subway_payroll_id}-${index}`}>
                                    {person.employee_name}: {Number(person.hours_worked || 0)} hours,
                                    {" "}{currency(person.payroll_amount)}
                                    {!person.mapped && " (unmapped)"}
                                  </li>
                                ))}</ul>
                              ) : <p>No hours for this date.</p>}
                              {row.unmapped_count > 0 && <p role="status">{row.unmapped_count} unmapped payroll entries</p>}
                            </section>
                            <section>
                              <h2>Fixed costs</h2>
                              <ul>
                                {(row.fixed_cost_rows?.amount_rows || []).map((cost) => (
                                  <li key={`amount-${cost.id}`}>{cost.cost_name}: {currency(cost.daily_amount)} per day</li>
                                ))}
                                {(row.fixed_cost_rows?.percentage_rows || []).map((cost) => (
                                  <li key={`percent-${cost.id}`}>{cost.cost_name}: {cost.percentage}% ({currency(cost.calculated_amount)})</li>
                                ))}
                              </ul>
                              {!row.fixed_cost_rows?.amount_rows?.length && !row.fixed_cost_rows?.percentage_rows?.length && <p>No active fixed costs.</p>}
                            </section>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">All stores with data</th>
                  {fields.map(([key]) => <td
  key={key}
  className={key === "profit_loss_total" && report.totals?.[key] < 0
    ? "subway-all-negative"
    : ""}
>
  {currency(report.totals?.[key])}
</td>)}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
